import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../context/AuthContext';
import { Card, Button, Spinner, Badge, Modal } from '../../components/UI';
import { integrationsAPI } from '../../services/api';
import toast from 'react-hot-toast';
import {
  HiOutlineLink, HiOutlineExternalLink, HiOutlineRefresh, HiOutlineClipboardCopy,
  HiOutlineCheck, HiOutlineX, HiOutlineExclamation, HiOutlineCog,
  HiOutlineDocumentText, HiOutlineChartBar, HiOutlinePlus
} from 'react-icons/hi';

// Integration type configurations
const INTEGRATION_TYPES = [
  {
    id: 'indiamart',
    name: 'IndiaMART',
    description: 'Import leads from IndiaMART via webhook',
    icon: '🛒',
    color: '#FF5722',
    credentials: ['crmId', 'authKey'],
    credentialsLabels: { crmId: 'CRM ID', authKey: 'Auth Key' }
  },
  {
    id: 'justdial',
    name: 'JustDial',
    description: 'Import leads from JustDial via webhook',
    icon: '📞',
    color: '#4CAF50',
    credentials: ['clientId', 'clientSecret'],
    credentialsLabels: { clientId: 'Client ID', clientSecret: 'Client Secret' }
  },
  {
    id: 'meta_lead_ads',
    name: 'Meta Lead Ads',
    description: 'Import leads from Facebook/Instagram Lead Ads',
    icon: '📘',
    color: '#1877F2',
    credentials: ['appId', 'appSecret', 'pageId'],
    credentialsLabels: { appId: 'App ID', appSecret: 'App Secret', pageId: 'Page ID' }
  },
  {
    id: 'custom_webhook',
    name: 'Custom Webhook',
    description: 'Receive leads from any custom webhook',
    icon: '🔗',
    color: '#9C27B0',
    credentials: [],
    credentialsLabels: {}
  }
];

// Status badge component
const StatusBadge = ({ status }) => {
  const statusStyles = {
    active: 'bg-green-100 text-green-700',
    pending_verification: 'bg-amber-100 text-amber-700 border border-amber-300',
    paused: 'bg-gray-100 text-gray-700',
    error: 'bg-red-100 text-red-700',
    disabled: 'bg-gray-100 text-gray-500'
  };

  const statusLabels = {
    active: 'Active',
    pending_verification: '⚠ Needs Activation',
    paused: 'Paused',
    error: 'Error',
    disabled: 'Disabled'
  };

  return (
    <Badge className={statusStyles[status] || statusStyles.paused}>
      {statusLabels[status] || status}
    </Badge>
  );
};

// Health indicator component
const HealthIndicator = ({ health }) => {
  if (!health) return null;

  const statusColors = {
    healthy: 'bg-green-500',
    degraded: 'bg-yellow-500',
    unhealthy: 'bg-red-500',
    unknown: 'bg-gray-300'
  };

  return (
    <div className="flex items-center gap-1.5">
      <div className={`w-2 h-2 rounded-full ${statusColors[health.status] || statusColors.unknown}`} />
      <span className="text-xs text-gray-500 capitalize">{health.status}</span>
    </div>
  );
};

// Integration Card Component
const IntegrationCard = ({ integration, onConfigure, onToggle, onViewLogs, onTest }) => {
  const typeConfig = INTEGRATION_TYPES.find(t => t.id === integration.type) || INTEGRATION_TYPES[3];
  const [copied, setCopied] = React.useState(false);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Card padding="p-5" className="hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-12 h-12 rounded-lg flex items-center justify-center text-2xl"
            style={{ backgroundColor: `${typeConfig.color}20` }}
          >
            {typeConfig.icon}
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{integration.name || typeConfig.name}</h3>
            <p className="text-sm text-gray-500">{typeConfig.description}</p>
          </div>
        </div>
        <StatusBadge status={integration.status} />
      </div>

      {/* Webhook URL */}
      {integration.webhookUrl && (
        <div className="mb-4">
          <label className="block text-xs font-medium text-gray-500 mb-1">Webhook URL</label>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-xs bg-gray-100 px-2 py-1.5 rounded truncate text-gray-700">
              {integration.webhookUrl}
            </code>
            <button
              onClick={() => copyToClipboard(integration.webhookUrl)}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors"
              title="Copy webhook URL"
            >
              {copied ? (
                <HiOutlineCheck className="w-4 h-4 text-green-600" />
              ) : (
                <HiOutlineClipboardCopy className="w-4 h-4" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="text-center p-2 bg-gray-50 rounded">
          <div className="text-lg font-semibold text-gray-900">
            {integration.stats?.totalImported || 0}
          </div>
          <div className="text-xs text-gray-500">Imported</div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <div className="text-lg font-semibold text-gray-900">
            {integration.stats?.totalDuplicates || 0}
          </div>
          <div className="text-xs text-gray-500">Duplicates</div>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded">
          <div className="text-lg font-semibold text-gray-900">
            {integration.stats?.totalErrors || 0}
          </div>
          <div className="text-xs text-gray-500">Errors</div>
        </div>
      </div>

      {/* Last Activity */}
      <div className="flex items-center justify-between text-sm text-gray-500 mb-4">
        <span>Last synced:</span>
        <span>
          {integration.stats?.lastProcessedAt
            ? new Date(integration.stats.lastProcessedAt).toLocaleString()
            : 'Never'}
        </span>
      </div>

      {/* Health */}
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm text-gray-500">Health:</span>
        <HealthIndicator health={integration.health} />
      </div>

      {/* Error Message */}
      {(integration.lastError?.message || integration.health?.errorMessage) && (
        <div className="bg-red-50 border border-red-200 rounded p-2 mb-4">
          <div className="flex items-center gap-1.5 text-red-700 text-sm">
            <HiOutlineExclamation className="w-4 h-4" />
            <span className="font-medium">Last Error:</span>
          </div>
          <p className="text-xs text-red-600 mt-1 truncate">
            {integration.lastError?.message || integration.health?.errorMessage}
          </p>
        </div>
      )}

      {/* Pending Verification Notice */}
      {integration.status === 'pending_verification' && (
        <div className="bg-amber-50 border border-amber-200 rounded p-3 mb-4">
          <div className="flex items-start gap-2">
            <HiOutlineExclamation className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-xs text-amber-700 font-medium">Action Required</p>
              <p className="text-xs text-amber-600 mt-1">
                {typeConfig?.credentials?.length
                  ? `Configure ${typeConfig.name} credentials and click Activate to start receiving leads.`
                  : 'Click Activate to start receiving leads via webhook.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          icon={<HiOutlineCog className="w-4 h-4" />}
          onClick={() => onConfigure(integration)}
        >
          Configure
        </Button>

        {integration.status === 'pending_verification' ? (
          <Button
            variant="outline"
            size="sm"
            className="text-green-600 border-green-300 hover:bg-green-50"
            onClick={() => onToggle(integration, true)}
          >
            Activate
          </Button>
        ) : integration.isActive ? (
          <Button
            variant="outline"
            size="sm"
            className="text-yellow-600 border-yellow-300 hover:bg-yellow-50"
            onClick={() => onToggle(integration, false)}
          >
            Disable
          </Button>
        ) : (
          <Button
            variant="outline"
            size="sm"
            className="text-green-600 border-green-300 hover:bg-green-50"
            onClick={() => onToggle(integration, true)}
          >
            Enable
          </Button>
        )}

        <Button
          variant="ghost"
          size="sm"
          icon={<HiOutlineDocumentText className="w-4 h-4" />}
          onClick={() => onViewLogs(integration)}
          title="View Logs"
        >
          Logs
        </Button>

        <Button
          variant="ghost"
          size="sm"
          icon={<HiOutlineRefresh className="w-4 h-4" />}
          onClick={() => onTest(integration)}
          title="Test Connection"
        >
          Test
        </Button>
      </div>
    </Card>
  );
};

// Configuration Modal Component
const IntegrationModal = ({ isOpen, onClose, integration, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    type: 'custom_webhook',
    credentials: {},
    defaultValues: {
      status: 'new',
      priority: 'medium',
      stage: 'new_lead',
      source: '',
      autoAssign: false,
      assignTo: ''
    },
    assignmentRules: {
      enabled: false,
      strategy: 'round_robin',
      assignToUserIds: []
    },
    duplicateHandling: {
      enabled: true,
      matchBy: ['phone'],
      action: 'skip'
    },
    notifications: {
      onNewLead: true,
      notifyAdmins: true,
      notifyAssignedUser: true
    }
  });

  const [webhookUrl, setWebhookUrl] = useState('');

  React.useEffect(() => {
    if (integration) {
      // Get credentials nested by type (e.g., credentials.indiamart.crmId)
      const typeCredentials = integration.credentials?.[integration.type] || integration.credentials || {};

      // Handle masked credentials - show placeholder for existing credentials
      const processedCredentials = {};
      Object.keys(typeCredentials).forEach(key => {
        // Skip masked versions, use original if available
        if (key.endsWith('Masked')) {
          const originalKey = key.replace('Masked', '');
          if (!typeCredentials[originalKey]) {
            // Original was deleted, show placeholder indicating credential exists
            processedCredentials[originalKey] = ''; // Empty to let user re-enter
          }
        } else {
          processedCredentials[key] = typeCredentials[key] || '';
        }
      });

      setFormData({
        name: integration.name || '',
        type: integration.type || 'custom_webhook',
        credentials: processedCredentials,
        defaultValues: integration.defaultValues || {},
        assignmentRules: integration.assignmentRules || {},
        duplicateHandling: integration.duplicateHandling || {},
        notifications: integration.notifications || {}
      });
      // Use webhookUrl from API response
      setWebhookUrl(integration.webhookUrl || '');
    } else {
      setFormData({
        name: '',
        type: 'custom_webhook',
        credentials: {},
        defaultValues: {
          status: 'new',
          priority: 'medium',
          stage: 'new_lead',
          source: '',
          autoAssign: false,
          assignTo: ''
        },
        assignmentRules: {
          enabled: false,
          strategy: 'round_robin',
          assignToUserIds: []
        },
        duplicateHandling: {
          enabled: true,
          matchBy: ['phone'],
          action: 'skip'
        },
        notifications: {
          onNewLead: true,
          notifyAdmins: true,
          notifyAssignedUser: true
        }
      });
      setWebhookUrl('');
    }
  }, [integration, isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  const typeConfig = INTEGRATION_TYPES.find(t => t.id === formData.type);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={integration ? 'Edit Integration' : 'New Integration'}
      size="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Settings */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Basic Settings</h4>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Integration Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              disabled={!!integration}
            >
              {INTEGRATION_TYPES.map(type => (
                <option key={type.id} value={type.id}>{type.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Configuration Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              placeholder={`${typeConfig?.name || 'Integration'} Configuration`}
              required
            />
          </div>
        </div>

        {/* Webhook URL */}
        {integration && webhookUrl && (
          <div className="space-y-2">
            <h4 className="font-medium text-gray-900">Webhook URL</h4>
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={webhookUrl}
                readOnly
                className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                icon={<HiOutlineClipboardCopy className="w-4 h-4" />}
                onClick={() => copyToClipboard(webhookUrl)}
              >
                Copy
              </Button>
            </div>
            <p className="text-xs text-gray-500">
              Configure your {typeConfig?.name} to send leads to this URL
            </p>
          </div>
        )}

        {/* Credentials */}
        {typeConfig?.credentials && typeConfig.credentials.length > 0 && (
          <div className="space-y-4">
            <h4 className="font-medium text-gray-900">API Credentials</h4>
            {typeConfig.credentials.map(cred => (
              <div key={cred}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {typeConfig.credentialsLabels[cred] || cred}
                </label>
                <input
                  type="text"
                  value={formData.credentials[cred] || ''}
                  onChange={(e) => setFormData({
                    ...formData,
                    credentials: { ...formData.credentials, [cred]: e.target.value }
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                  placeholder={`Enter ${typeConfig.credentialsLabels[cred] || cred}`}
                />
              </div>
            ))}
          </div>
        )}

        {/* Default Values */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Default Lead Values</h4>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Default Status
              </label>
              <select
                value={formData.defaultValues.status}
                onChange={(e) => setFormData({
                  ...formData,
                  defaultValues: { ...formData.defaultValues, status: e.target.value }
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="new">New</option>
                <option value="open">Open</option>
                <option value="in_progress">In Progress</option>
                <option value="follow_up">Follow Up</option>
                <option value="cold">Cold</option>
                <option value="warm">Warm</option>
                <option value="hot">Hot</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={formData.defaultValues.priority}
                onChange={(e) => setFormData({
                  ...formData,
                  defaultValues: { ...formData.defaultValues, priority: e.target.value }
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Lead Source
              </label>
              <input
                type="text"
                value={formData.defaultValues.source || typeConfig?.name || ''}
                onChange={(e) => setFormData({
                  ...formData,
                  defaultValues: { ...formData.defaultValues, source: e.target.value }
                })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                placeholder={typeConfig?.name || 'Source'}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Auto Assign
              </label>
              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  checked={formData.defaultValues.autoAssign}
                  onChange={(e) => setFormData({
                    ...formData,
                    defaultValues: { ...formData.defaultValues, autoAssign: e.target.checked }
                  })}
                  className="h-4 w-4 text-primary-600 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Automatically assign leads</span>
              </div>
            </div>
          </div>
        </div>

        {/* Duplicate Handling */}
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Duplicate Handling</h4>

          <div className="flex items-center gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.duplicateHandling.enabled}
                onChange={(e) => setFormData({
                  ...formData,
                  duplicateHandling: { ...formData.duplicateHandling, enabled: e.target.checked }
                })}
                className="h-4 w-4 text-primary-600 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Enable duplicate detection</span>
            </label>
          </div>

          {formData.duplicateHandling.enabled && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Match By
                </label>
                <select
                  value={formData.duplicateHandling.matchBy[0] || 'phone'}
                  onChange={(e) => setFormData({
                    ...formData,
                    duplicateHandling: { ...formData.duplicateHandling, matchBy: [e.target.value] }
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="phone">Phone Number</option>
                  <option value="email">Email Address</option>
                  <option value="phone_email">Phone or Email</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Action on Duplicate
                </label>
                <select
                  value={formData.duplicateHandling.action}
                  onChange={(e) => setFormData({
                    ...formData,
                    duplicateHandling: { ...formData.duplicateHandling, action: e.target.value }
                  })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                >
                  <option value="skip">Skip (do not import)</option>
                  <option value="update">Update existing lead</option>
                  <option value="create_duplicate">Create duplicate</option>
                </select>
              </div>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit">
            {integration ? 'Save Changes' : 'Create Integration'}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

// Logs Modal Component
const LogsModal = ({ isOpen, onClose, integration }) => {
  const [filters, setFilters] = useState({ status: '', page: 1 });

  const { data, isLoading } = useQuery({
    queryKey: ['integrationLogs', integration?._id, filters],
    queryFn: async () => {
      if (!integration) return null;
      const response = await integrationsAPI.getLogs(integration._id, filters);
      return response.data;
    },
    enabled: isOpen && !!integration
  });

  const statusStyles = {
    success: 'bg-green-100 text-green-700',
    partial_success: 'bg-yellow-100 text-yellow-700',
    failed: 'bg-red-100 text-red-700',
    duplicate: 'bg-blue-100 text-blue-700',
    error: 'bg-red-100 text-red-700'
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Logs - ${integration?.name || ''}`} size="xl">
      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <select
          value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
          className="border border-gray-300 rounded-md px-3 py-2 text-sm"
        >
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="partial_success">Partial Success</option>
          <option value="failed">Failed</option>
          <option value="duplicate">Duplicate</option>
          <option value="error">Error</option>
        </select>

        <div className="flex-1" />

        <div className="text-sm text-gray-500">
          Total: {data?.pagination?.total || 0} logs
        </div>
      </div>

      {/* Logs Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner size="lg" />
        </div>
      ) : !data?.data?.length ? (
        <div className="text-center py-8 text-gray-500">
          No logs found
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 px-3">Date/Time</th>
                <th className="py-2 px-3">Status</th>
                <th className="py-2 px-3">Received</th>
                <th className="py-2 px-3">Imported</th>
                <th className="py-2 px-3">Duplicates</th>
                <th className="py-2 px-3">Errors</th>
                <th className="py-2 px-3">Duration</th>
                <th className="py-2 px-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {data?.data?.map((log, index) => (
                <tr key={log._id || index} className="border-b">
                  <td className="py-2 px-3 whitespace-nowrap">
                    {new Date(log.createdAt).toLocaleString()}
                  </td>
                  <td className="py-2 px-3">
                    <Badge className={statusStyles[log.status] || 'bg-gray-100 text-gray-700'}>
                      {log.status}
                    </Badge>
                  </td>
                  <td className="py-2 px-3">{log.leads?.received || 0}</td>
                  <td className="py-2 px-3 text-green-600">{log.leads?.imported || 0}</td>
                  <td className="py-2 px-3 text-blue-600">{log.leads?.duplicates || 0}</td>
                  <td className="py-2 px-3 text-red-600">{log.leads?.errors || 0}</td>
                  <td className="py-2 px-3">{log.processing?.durationMs || 0}ms</td>
                  <td className="py-2 px-3">
                    {log.error?.message ? (
                      <span className="text-red-600 truncate block max-w-xs" title={log.error.message}>
                        {log.error.message}
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {data?.pagination?.pages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            size="sm"
            disabled={filters.page <= 1}
            onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
          >
            Previous
          </Button>
          <span className="px-4 py-2 text-sm">
            Page {filters.page} of {data.pagination.pages}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={filters.page >= data.pagination.pages}
            onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
          >
            Next
          </Button>
        </div>
      )}
    </Modal>
  );
};

// Main Component
const Integrations = () => {
  const { isAuthenticated, isInitialized } = useAuth();
  const queryClient = useQueryClient();
  const [selectedIntegration, setSelectedIntegration] = useState(null);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [isLogsModalOpen, setIsLogsModalOpen] = useState(false);
  const [logsIntegration, setLogsIntegration] = useState(null);

  // Fetch integrations
  const { data: integrations, isLoading } = useQuery({
    queryKey: ['integrations'],
    queryFn: async () => {
      const response = await integrationsAPI.getAll();
      return response.data?.data || [];
    },
    enabled: isAuthenticated && isInitialized
  });

  // Create mutation
  const createMutation = useMutation({
    mutationFn: (data) => integrationsAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      toast.success('Integration created successfully');
      setIsConfigModalOpen(false);
      setSelectedIntegration(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create integration');
    }
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => integrationsAPI.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['integrations']);
      toast.success('Integration updated successfully');
      setIsConfigModalOpen(false);
      setSelectedIntegration(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to update integration');
    }
  });

  // Toggle mutation
  const toggleMutation = useMutation({
    mutationFn: ({ id, enable }) =>
      enable ? integrationsAPI.enable(id) : integrationsAPI.disable(id),
    onSuccess: (response) => {
      queryClient.invalidateQueries(['integrations']);

      // Show warning if credentials not configured
      if (response.data?.warning) {
        toast.error(response.data.message, { duration: 6000 });
      } else {
        toast.success(response.data?.message || 'Integration updated');
      }
    },
    onError: (error) => {
      const message = error.response?.data?.message || 'Failed to update integration';
      toast.error(message);
    }
  });

  // Test mutation
  const testMutation = useMutation({
    mutationFn: (id) => integrationsAPI.test(id),
    onSuccess: (response) => {
      if (response.data?.success) {
        toast.success('Connection test successful');
      } else {
        toast.error(response.data?.message || 'Connection test failed');
      }
      queryClient.invalidateQueries(['integrations']);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Connection test failed');
    }
  });

  const handleConfigure = (integration) => {
    setSelectedIntegration(integration);
    setIsConfigModalOpen(true);
  };

  const handleToggle = (integration, enable) => {
    const isActivation = integration.status === 'pending_verification' && enable;
    const message = isActivation
      ? 'Activate this integration? This will allow it to start receiving leads via webhook.'
      : `Are you sure you want to ${enable ? 'enable' : 'disable'} this integration?`;

    if (window.confirm(message)) {
      toggleMutation.mutate({ id: integration._id, enable });
    }
  };

  const handleViewLogs = (integration) => {
    setLogsIntegration(integration);
    setIsLogsModalOpen(true);
  };

  const handleTest = (integration) => {
    testMutation.mutate(integration._id);
  };

  const handleSave = (formData) => {
    // Nest credentials under integration type (e.g., { indiamart: { crmId, authKey } })
    const typeConfig = INTEGRATION_TYPES.find(t => t.id === formData.type);
    const nestedCredentials = typeConfig?.credentials?.length
      ? { [formData.type]: formData.credentials }
      : formData.credentials;

    const dataToSave = {
      ...formData,
      credentials: nestedCredentials
    };

    if (selectedIntegration) {
      updateMutation.mutate({ id: selectedIntegration._id, data: dataToSave });
    } else {
      createMutation.mutate(dataToSave);
    }
  };

  if (!isAuthenticated || !isInitialized) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Integrations</h3>
          <p className="text-gray-500 text-sm mt-1">
            Connect third-party platforms to automatically import leads
          </p>
        </div>
        <Button
          icon={<HiOutlinePlus className="w-4 h-4" />}
          onClick={() => {
            setSelectedIntegration(null);
            setIsConfigModalOpen(true);
          }}
        >
          Add Integration
        </Button>
      </div>

      {/* Integration Cards */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : !integrations?.length ? (
        <Card padding="p-8">
          <div className="text-center">
            <HiOutlineLink className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No integrations configured</h3>
            <p className="text-gray-500 mb-4">
              Connect your lead sources to automatically import leads into your CRM
            </p>
            <Button
              icon={<HiOutlinePlus className="w-4 h-4" />}
              onClick={() => {
                setSelectedIntegration(null);
                setIsConfigModalOpen(true);
              }}
            >
              Add Your First Integration
            </Button>
          </div>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {integrations.map(integration => (
            <IntegrationCard
              key={integration._id}
              integration={integration}
              onConfigure={handleConfigure}
              onToggle={handleToggle}
              onViewLogs={handleViewLogs}
              onTest={handleTest}
            />
          ))}
        </div>
      )}

      {/* Configuration Modal */}
      <IntegrationModal
        isOpen={isConfigModalOpen}
        onClose={() => {
          setIsConfigModalOpen(false);
          setSelectedIntegration(null);
        }}
        integration={selectedIntegration}
        onSave={handleSave}
      />

      {/* Logs Modal */}
      <LogsModal
        isOpen={isLogsModalOpen}
        onClose={() => {
          setIsLogsModalOpen(false);
          setLogsIntegration(null);
        }}
        integration={logsIntegration}
      />
    </div>
  );
};

export default Integrations;